<template>
  <div>
    <v-card :loading="loading" outlined flat style="min-width: 200px;">
      <v-card-title>
        <div>
          <v-progress-circular v-if="updating" indeterminate color="primary" :size="22" :width="2" />
          <v-icon v-else class="mr-2">{{ icon }}</v-icon>
          {{ title }}
        </div>
        <v-spacer/>
        <cp-help :i18n="helpKey"/>
      </v-card-title>
      <v-card-text>
        <div v-if="pbxDefaults" class="mt-1">
          <v-icon small class="mr-2">mdi-book-music</v-icon>{{ $t('vmail.cfg.greeting.defaults') }}
        </div>
        <div v-else>
          <audio-player :src="audioSrc" v-if="audioSrc" class="py-2 mr-2" />
        </div>
        <div v-if="!canWrite || hasMultipleActiveWatchers"
          class="mt-4">
          <v-icon color="red">mdi-alert-box-outline</v-icon>
          {{ hasMultipleActiveWatchers ? $t('vmail.notify.hasMultipleWatchers') : $t('perms.nochange') }}
        </div>
      </v-card-text>
      <v-card-actions>
        <div flat v-if="pbxDefaults" style="width: 100%;">
          <div v-if="isOnMobile" class="d-flex justify-space-between">
            <v-btn icon :disabled="!canWrite || hasMultipleActiveWatchers" v-show="hasArchives"
              @click.stop="restoreArchive" class="mx-1">
              <v-icon color="primary">mdi-history</v-icon>
            </v-btn>
            <v-btn icon :disabled="!canWrite || hasMultipleActiveWatchers"
              @click.stop="uploadForm=true" class="mx-1">
              <v-icon color="primary">mdi-upload</v-icon>
            </v-btn>
            <v-btn icon :disabled="!canWrite || hasMultipleActiveWatchers" @click.stop="recording=true" class="mx-1">
              <v-icon color="primary">mdi-radiobox-marked</v-icon>
            </v-btn>
            <v-btn icon :disabled="!canWrite || hasMultipleActiveWatchers" @click.stop="showTTSForm=true" class="mx-1">
              <v-icon color="primary">mdi-text-box</v-icon>
            </v-btn>
          </div>
          <div v-else>
            <v-btn text :disabled="!canWrite || hasMultipleActiveWatchers" v-show="hasArchives" small
              @click.stop="restoreArchive" outlined class="mx-1">
              <v-icon class="mr-2" color="primary" small>mdi-history</v-icon>
              {{ $t('vmail.cfg.greeting.btnRestore') }}
            </v-btn>
            <v-btn text :disabled="!canWrite || hasMultipleActiveWatchers" small
              @click.stop="uploadForm=true" outlined class="mx-1">
              <v-icon class="mr-2" color="primary" small>mdi-upload</v-icon>
              {{ $t('vmail.cfg.greeting.btnUpload') }}
            </v-btn>
            <v-btn :disabled="!canWrite || hasMultipleActiveWatchers" text @click.stop="recording=true" small outlined class="mx-1">
              <v-icon class="mr-2" color="primary" small>mdi-radiobox-marked</v-icon>
              {{ $t('vmail.cfg.greeting.btnRecord') }}
            </v-btn>
            <v-btn :disabled="!canWrite || hasMultipleActiveWatchers" text @click.stop="showTTSForm=true" small outlined class="mx-1">
              <v-icon class="mr-2" color="primary" small>mdi-text-box</v-icon>
              {{ $t('vmail.cfg.greeting.btnTTS') }}
            </v-btn>
          </div>
        </div>
        <div flat v-else>
          <greeting-actions :canReset="!pbxDefaults" :canRestore="hasArchives"
            @reset="confirmReset=true" @upload="uploadForm=true"
            @restore="restoreArchive" @record="recording=true" @tts="showTTSForm=true" />
        </div>
      </v-card-actions>
    </v-card>
    <confirm-reset
      :show="confirmReset"
      :icon="icon"
      :name="title"
      @cancel="confirmReset=false"
      @confirm="resetArchive($event)"/>
    <file-upload
      :show="uploadForm"
      :name="title"
      @save-file="uploadGreeting($event)"
      @restore-archive="restoreArchive"
      @cancel="uploadForm=false"/>
    <recorder
      :title="$t('vmail.cfg.greeting.rec.title', { name: title })"
      :show="recording"
      @save-record="saveRecord($event)"
      @cancel="recording=false"/>
    <restore-form
      :name="apiName"
      @restored="setAudio"
      @close="restoreForm=false"
      :show="restoreForm"/>
    <tts-form
      v-if="apiName && watcherBoxID"
      :show="showTTSForm"
      :messageType="apiName"
      :voicemailBoxID="watcherBoxID"
      :default-text="defaultTtsText"
      @set-audio="setAudio"
      @updating="updating=!updating"
      @close="showTTSForm=false"/>
  </div>
</template>

<script>
import API from '@/api'
import events from 'Store/events'
import ConfirmReset from './confirm_reset'
import Recorder from '@/components/recorder'
import FileUpload from './upload'
import RestoreForm from './restore_form'
import GreetingActions from './card_actions'
import TtsForm from './tts_form'
import AudioPlayer from '@/components/audio_player'
import { mapGetters, mapActions } from 'vuex'

export default {
  name: 'vmail-greeting-card',
  data () {
    return {
      audioSrc: null,
      confirmReset: false,
      uploadForm: false,
      restoreForm: false,
      recording: false,
      showTTSForm: false,
      updating: false,
      loading: false
    }
  },
  props: {
    title: { type: String },
    icon: { type: String },
    apiName: { type: String, required: true },
    helpKey: { type: String },
    defaultTtsText: { type: String, required: false }
  },
  computed: {
    ...mapGetters({
      vmArchives: 'voicemails/archives',
      msgID: 'voicemails/greetMsgID',
      currentUserID: 'settings/userID',
      vmboxWriteFor: 'permissions/vmboxWriteFor',
      vmboxReadFor: 'permissions/vmboxReadFor',
      canReadCurrentUser: 'permissions/vmboxesRead',
      canWriteCurrentUser: 'permissions/vmboxesWrite',
      watcherUserID: 'voicemails/watcherUserID',
      watcherBoxID: 'voicemails/watcherBoxID',
      vmWatcherSubscriberID: 'voicemails/watcherSubscriberID',
      vmWatcherUserName: 'voicemails/watcherUserName',
      activeWatchers: 'voicemails/activeWatchers'
    }),
    canWrite: function () {
      if (this.currentUserID === this.watcherUserID) {
        return this.canWriteCurrentUser
      }
      return this.vmboxWriteFor(this.vmWatcherSubscriberID)
    },
    canRead: function () {
      if (this.currentUserID === this.watcherUserID) {
        return this.canReadCurrentUser
      }
      return this.vmboxReadFor(this.watcherBoxID)
    },
    folderName: function () {
      switch (this.apiName) {
        case 'unavailable': return 'unavail'
        case 'busy': return 'busy'
        case 'greeting': return 'greet'
        case 'temp': return 'temp'
        default: return 'UNKNOWN'
      }
    },
    pbxDefaults: function () {
      return this.audioSrc === null
    },
    hasArchives: function () {
      return this.vmArchives?.length > 0
    },
    watcherUser: function () {
      const watcherUserID = this.watcherUserID
      return watcherUserID === this.currentUserID
        ? null
        : this.vmWatcherUserName
    },
    hasMultipleActiveWatchers: function () {
      return this.activeWatchers.length > 1
    },
    isOnMobile () { return this.$vuetify.breakpoint.mobile }
  },
  components: {
    ConfirmReset,
    FileUpload,
    Recorder,
    RestoreForm,
    GreetingActions,
    TtsForm,
    AudioPlayer
  },
  mounted () {
    this.setAudio()
    this.$root.$on('load-vm-greetings', this.setAudio)
  },
  methods: {
    ...mapActions({
      setGreetData: 'voicemails/setGreetData',
      loadArchives: 'voicemails/loadArchives'
    }),

    setAudio: function () {
      this.clearCard()
      this.loading = true
      API.VMGreetingGet(this.apiName, this.watcherBoxID).then(response => {
        if (!/^audio/.test(response.data?.type)) {
          return // greeting does not exists
        }
        this.setAudioPlayer(response.data)

        if (this.canRead) {
          return API.getVMByFolder(this.activeWatchers < 1 ? [this.sipuaExten] : [...new Set(this.activeWatchers.map(w => w.username))], this.folderName)
        }
      }).then(data => {
        this.setGreetData({ data: data?.data, folder: this.folderName })
      }).catch(error => {
        this.dbg(error)
        this.dbg(`Failed go get voicemail recording: ${this.apiName}`)
      }).finally(() => {
        this.loading = false
      })
    },

    setAudioPlayer: function (data) {
      this.audioSrc = URL.createObjectURL(data)
    },

    resetArchive: function ({ archive, archName }) {
      if (!archive) { // just reset
        this.reset()
        return
      }
      // archive
      const mid = this.msgID(this.apiName)
      this.dbg(`Archive message id ${mid} with name '${archName}'`)
      API.archiveVMail(mid, archName).then(() => {
        this.reset()
        this.loadArchives()
        events.pubNotify({ text: 'vmail.cfg.greeting.notify.archived', status: 'success' })
      }).catch(error => {
        this.dbg(error)
        events.pubError(`Failed to archive voicemail recording: ${this.apiName}`)
      })
    },
    reset: function () {
      API.VMGreetingDel(this.apiName, this.watcherBoxID).then(response => {
        this.clearCard()
        events.pubNotify({ text: 'vmail.cfg.greeting.notify.reset', status: 'success' })
      }).catch(error => {
        this.dbg(error)
        events.pubError(`Failed go delete voicemail recording: ${this.apiName}`)
      })
    },
    saveRecord: function (file) {
      this.recording = false
      const data = new FormData()
      data.append(`${this.apiName}_file`, file)
      this.uploadFile(data)
    },
    uploadGreeting: function (file) {
      this.uploadForm = false
      const data = new FormData()
      data.append(`${this.apiName}_file`, file)
      this.uploadFile(data)
    },
    uploadFile: function (data) {
      this.updating = true
      API.VMGreetingSet(this.apiName, this.watcherBoxID, data).then(response => {
        events.pubNotify({
          text: 'vmail.cfg.greeting.updated',
          args: { name: this.title },
          status: 'success'
        })
        this.setAudio()
      }).catch(error => {
        this.dbg(error)
        this.dbg(`Failed go get voicemail recording: ${this.apiName}`)
        const msg = error.response?.data?.message
          ? error.response.data.message
          : `Failed updload file for: ${this.apiName}`
        events.pubError(msg)
      }).finally(() => {
        this.updating = false
      })
    },
    restoreArchive: function () {
      this.restoreForm = true
    },
    clearCard: function () {
      this.audioSrc = null
      this.confirmReset = false
    }
  }
}
</script>

<style scoped>
</style>
